/* Client Hub Overview — the owner-ratified presence-structured Overview
 * (docs/superpowers/specs/2026-07-16-client-hub-layout-design.md §4-§9).
 *
 * REPLACES the old aspect-grid / five-zone overview (retired with panels.ts +
 * embed.ts). The client — not the run — is the object: this is one integrated
 * presence map, not a room per research aspect. Locked top-to-bottom order:
 *
 *   1. Header strip — descriptor + connector freshness (News "not wired")
 *   2. Program Position — synthesis narrative + Baseline Score, else honest
 *   3. At a glance — curated stat cards from REAL payload data only
 *   4. Entity Core — identity facts (each with a source dot), else honest
 *   5. Presence Map — entity-first tree (Websites · Social · Reviews &
 *      Directories · Community & Reference · Media) + [Tree | Graph] toggle;
 *      Wikipedia/Wikidata excluded (they live in card 6). Node click → drawer.
 *   6. Wikipedia & Wikidata card — the managed entity / service line
 *   7. How the Reputation Surfaces — Search · News · AI contribution cards
 *   8. What Needs Improving — dark assets · opportunities · run-asserted
 *      conflicts · structured risks (none this run)
 *   9. Coverage footer — per-plane coverage + the standing receipt line
 *   + narrow right CRM sidecard rail — Relationship · Meetings · Documents ·
 *     Jira, all honest "not connected" (spec §3).
 *
 * HONESTY SPINE (spec §11): every number traces to the loaded payload; every
 * unbuilt producer renders "not generated | not connected | not tracked | not
 * measured", never a fabricated zero. News is never a number. Peers never enter
 * a subject rollup. Contribution attribution is MEASURED (derive/contribution.ts,
 * the lifted layer); modeled potential is not shown here as a metric.
 *
 * Reuse, don't fork (spec §11): the measured link map is ui/graph.ts's island
 * (initGraph → the #map svg) behind the Graph toggle; a node click opens the
 * universal ui/drawer.ts asset drawer (transitional until the property-drawer
 * port). No second task store, no renderer edits.
 */
import { html, nothing, type TemplateResult } from 'lit';
import type { AppPayloadState } from '../../derive/state-shape.ts';
import type { Workspace } from '../../state/workspace.ts';
import type {
  AssetType, FactEntry, NormalizedAsset,
} from '../../types/contract.ts';
import {
  computeContribution, contributionFor, entityContribution, rankChip,
  searchAttribution, type ContributionMap,
} from '../../derive/contribution.ts';
import { matrixEntities, entityMapDeclared, type MatrixEntityRow } from '../../derive/entity.ts';
import { aiHeadline } from '../../derive/ai.ts';
import { opportunityTitle } from '../../derive/actions.ts';
import { getDecisions } from '../../api/decisions.ts';
import { renderSignalsStrip } from './signalsStrip.ts';
import { brandVisual } from '../../lib/identity.ts';
import { brandLogo } from '../register.ts';
import { coveragePanel } from '../../derive/coverage.ts';
import { factSrc, factVal, pct } from '../../lib/format.ts';
import { initGraph } from '../../ui/graph.ts';
import { payloadStore } from '../../state/payload.ts';
import { WS } from '../../state/workspace.ts';
import { currentRoute } from '../../lib/router.ts';
import { closePropertyDrawer, openPropertyDrawer } from './propertyDrawer.ts';
import type { Unsubscribe } from '../../state/store.ts';
import {
  extractableUrls,
  extractionState,
  resetExtraction,
  runExtraction,
  subscribeExtraction,
} from '../../state/pageFields.ts';
import type { FieldConflict } from '../../api/pageFields.ts';
import type { CockpitClientRow, CockpitPlaneState } from './data.ts';
import type { ChangesSummaryResponse } from '../../api/changesSummary.ts';
import { buildTaskHref } from '../../ws/taskLink.ts';
import {
  claimItems,
  deleteLock,
  getProgramMemory,
  getProgramMemoryDiff,
  postLock,
  type ProgramMemoryDiff,
  type ProgramMemoryResponse,
} from '../../api/memory.ts';

/** A client deep-surface route for the client currently in the cockpit
 * (falls back to the legacy #/research/* section when there's no slug). */
function surfaceHref(surface: 'wiki' | 'search' | 'ai' | 'news'): string {
  const slug = currentRoute().slug;
  if (slug) return `#/client/${slug}/${surface}`;
  return surface === 'wiki' ? '#/research/wiki' : surface === 'ai' ? '#/research/ai' : '#/research/search';
}
function wikiHref(): string { return surfaceHref('wiki'); }

export interface OverviewDeps {
  ws: Workspace;
  state: AppPayloadState;
  row: CockpitClientRow | null;
  slug: string;
  /** re-render the whole overview (Tree/Graph toggle · sub-entity expand). */
  rerender: () => void;
  /** the §1 what-changed feed (unused in the locked order today; kept for the
   * Program Position line once synthesis lands). */
  changes: ChangesSummaryResponse | null;
  changesLoading: boolean;
}

/* ── presence-map view state (module-level; reset on teardown) ─────────────── */
type PMView = 'tree' | 'graph';
let pmView: PMView = 'tree';
const expandedEntities = new Set<string>();
/** channels (keyed `${entityKey}:${channelKey}`) the AM has expanded past the
 * default cap — a long channel group collapses to its top handful + "+N more". */
const expandedChannels = new Set<string>();
/** the "All entities ▾" filter on What Needs Improving ('' = all). */
let niEntityFilter = '';

// W1-D: the Overview's Intelligence-Plan block shows REAL decision counts. A tiny
// per-client cache (fetched once, refreshed on client switch) — honest null until
// loaded, never a fabricated count.
let ovDecCount: number | null = null;
let ovDecSlug: string | null = null;
let ovDecLoading = false;
function ensureOvDecisions(slug: string, rerender: () => void): void {
  if (ovDecSlug === slug || ovDecLoading) return;
  ovDecLoading = true;
  ovDecSlug = slug;
  ovDecCount = null;
  getDecisions(slug)
    .then((rows) => { if (ovDecSlug === slug) ovDecCount = rows.length; })
    .catch(() => { ovDecCount = null; })
    .finally(() => { ovDecLoading = false; rerender(); });
}

/** Reset presence-map UI state and strip any lingering graph <svg id="map">
 * from the cockpit, so a later legacy #/research/profiles render (which also
 * uses the singleton #map id) is never captured by a stale cockpit node. */
const MEASURED_CONFLICT_PHRASE: Record<string, string> = {
  'nap-mismatch': 'differs across properties',
  'website-link-mismatch': 'Official website link differs across properties',
  'divergent-description': 'Description differs across properties',
  'outdated-logo': 'Logo image differs across properties',
};
function measuredConflictTitle(cf: FieldConflict): string {
  if (cf.type === 'nap-mismatch') {
    const f = cf.field.charAt(0).toUpperCase() + cf.field.slice(1);
    return `${f} differs across properties`;
  }
  return MEASURED_CONFLICT_PHRASE[cf.type] || `${cf.field} differs across properties`;
}
function measuredConflictEvidence(cf: FieldConflict): string {
  const vals = (cf.values || [])
    .map((v) => (Array.isArray(v.value) ? v.value.join('/') : v.value))
    .filter(Boolean)
    .slice(0, 2);
  return vals.length >= 2 ? `${vals[0]} ≠ ${vals[1]}` : 'extracted values disagree';
}

/** One shared subscription so the extraction store settling re-paints the
 * Overview (mirrors ensureOvDecisions). The latest rerender wins. */
let extRerender: (() => void) | null = null;
let extUnsub: Unsubscribe | null = null;
function ensureExtractionSub(rerender: () => void): void {
  extRerender = rerender;
  if (!extUnsub) extUnsub = subscribeExtraction(() => extRerender?.());
}

export function teardownOverview(): void {
  pmView = 'tree';
  expandedEntities.clear();
  expandedChannels.clear();
  niEntityFilter = '';
  if (extUnsub) { extUnsub(); extUnsub = null; }
  extRerender = null;
  resetExtraction();
  // FIX-audit F9: an open property drawer belongs to THIS client's run; close it
  // on teardown so it can never survive a slug/run change and show client A's
  // property over client B.
  closePropertyDrawer();
  document.querySelectorAll('#cockpitView #map').forEach((el) => el.remove());
}

/** Called by index.ts's paint() AFTER the overview renders: mounts the measured
 * link-map island into the freshly-rendered #map svg when Graph mode is active.
 * Re-runs on every paint so a background WS/payload re-render keeps the graph. */
export function mountOverviewIslands(state: AppPayloadState): void {
  if (pmView === 'graph' && document.getElementById('map')) {
    try { initGraph(state); } catch { /* graph is best-effort — Tree is the default */ }
  }
}

/* ── shared section shell ──────────────────────────────────────────────────── */
function section(id: string, title: string, sub: string | null, body: TemplateResult, sourceNote?: string): TemplateResult {
  return html`<section class="hub-sec" data-hub=${id}>
    <div class="hub-sechead">
      <div class="hub-sechead-l">
        <h2 class="hub-sectitle">${title}</h2>
        ${sub ? html`<span class="hub-secsub">${sub}</span>` : nothing}
      </div>
      ${sourceNote ? html`<span class="hub-srcnote" title="data provenance">${sourceNote}</span>` : nothing}
    </div>
    ${body}
  </section>`;
}

/* ── 1 · Header strip — descriptor + connector freshness ───────────────────── */
function freshChip(label: string, plane?: CockpitPlaneState | null): TemplateResult {
  const iso = plane?.lastRun;
  const age = iso ? Math.floor((Date.now() - Date.parse(iso)) / 864e5) : null;
  if (age === null || Number.isNaN(age)) return html`<i class="fresh-chip none">${label} · not run</i>`;
  const cls = age > 21 ? 'stale' : 'ok';
  const when = age <= 0 ? 'today' : `${age}d ago`;
  return html`<i class="fresh-chip ${cls}">${label} · ${when}</i>`;
}

function headerStrip(state: AppPayloadState, row: CockpitClientRow | null): TemplateResult {
  const descriptor = state.entity?.descriptor || null;
  const p = row?.planes || {};
  return html`<section class="hub-headstrip">
    <div class="hub-descriptor">${descriptor || html`<span class="hub-muted">No descriptor on record</span>`}</div>
    <div class="hub-fresh" title="connector freshness">
      ${freshChip('Presence', p.presence)}
      ${freshChip('Search', p.search)}
      ${freshChip('AI', p.ai)}
      <i class="fresh-chip none" title="IMPACT News has no data plane wired into the run payload">News · not wired</i>
    </div>
  </section>`;
}

/* ── 2 · Program Position ──────────────────────────────────────────────────── */
function programPosition(state: AppPayloadState): TemplateResult {
  const syn = state.synthesis;
  if (!syn || !syn.executiveNarrative?.length) {
    return section('program', 'Program position', 'the synthesised read on where this client stands',
      html`<div class="hub-prog"><p class="hub-narr-empty">Program synthesis was not generated on this run — the executive
        narrative and Baseline Score are honestly absent, never zeroed. They fill in when the synthesis
        layer is stamped.</p></div>`);
  }
  const bs = syn.baselineScore;
  const scoreBand = bs
    ? html`<div class="hub-score">
        <div class="hub-score-num">${bs.score}<small>/${bs.denominator}</small></div>
        <div class="hub-score-meta">${bs.measuredComponents} of ${bs.totalComponents} planes measured · ${bs.provisionalLabel}</div>
      </div>`
    : html`<p class="hub-muted">Baseline Score not measured on this run.</p>`;
  return section('program', 'Program position', 'the synthesised read on where this client stands',
    html`<div class="hub-prog">
      <div class="hub-narr">${syn.executiveNarrative.map((c) => html`<p title="provenance: ${c.provenance}">${c.text}</p>`)}</div>
      ${scoreBand}
    </div>`);
}

/* DESIGN-FIDELITY P1-3(b): a minimal inline-SVG sparkline. Only ever called
   with a REAL multi-point series (see ownSeries guard); values are 0..1. The
   viewBox is unitless user-space, stroke colour is a design token — no raw
   hex/px, no fabricated data. */
function sparkline(values: number[]): TemplateResult {
  const w = 96;
  const h = 22;
  const n = values.length;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const pts = values
    .map((v, i) => `${((i / (n - 1)) * w).toFixed(1)},${(h - 2 - ((v - min) / range) * (h - 4)).toFixed(1)}`)
    .join(' ');
  const rising = (values[n - 1] ?? 0) >= (values[0] ?? 0);
  return html`<svg class="glance-spark" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" role="img" aria-label="page-1 ownership trend">
    <polyline points=${pts} fill="none" stroke=${rising ? 'var(--green)' : 'var(--neg)'} stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
  </svg>`;
}

/* ── 3 · At a glance · customizable — the mockup's curated default card set ──── */
function glanceStrip(state: AppPayloadState, _row: CockpitClientRow | null, _contrib: ContributionMap): TemplateResult {
  // A card either carries a real measured value or renders its HONEST absent
  // state (spec §11) — the widget zone keeps the owner's 5 curated defaults even
  // where the producer is unbuilt (time-series / structured Risk), rather than
  // fabricating or silently dropping. Full customization is a separate workstream.
  const measured = (value: string | number, label: string, sub: string, spark?: TemplateResult): TemplateResult =>
    html`<div class="glance-card"><div class="glance-num">${value}</div>${spark ?? nothing}<div class="glance-lbl">${label}</div><div class="glance-sub">${sub}</div></div>`;
  const absent = (label: string, why: string): TemplateResult =>
    html`<div class="glance-card glance-absent"><div class="glance-num hub-muted">—</div><div class="glance-lbl">${label}</div><div class="glance-sub">${why}</div></div>`;

  const sa = searchAttribution(state);
  const ai = aiHeadline(state);
  // DESIGN-FIDELITY P1-3(b): a mini sparkline ONLY where a REAL multi-point
  // series exists — page-1 ownership over the polled window (impact.trends.
  // ownershipTimeline). A single-poll run has no series → no sparkline (never a
  // fabricated line); the honest number/absent state stays.
  const ownTl = (state.impact?.trends as { ownershipTimeline?: Array<{ owned?: number; slots?: number }> } | undefined)?.ownershipTimeline;
  const ownSeries = ownTl && ownTl.length >= 2
    ? ownTl.map((p) => (p.owned ?? 0) / (p.slots || 10))
    : null;
  const cards: TemplateResult[] = [
    sa ? measured(`${sa.ownedPct}%`, 'Page-1 ownership', `${sa.page1Owned} of ${sa.page1Slots} page-one slots`, ownSeries ? sparkline(ownSeries) : undefined)
      : absent('Page-1 ownership', 'no Search capture this run'),
    ai && ai.ownership != null ? measured(`${pct(ai.ownership)}%`, 'Ownership of AI citations', `over ${ai.total.toLocaleString()} citations · ${ai.engines} engines`)
      : absent('Ownership of AI citations', 'not measured this run'),
    absent('Position over time', 'not measured — needs run history'),
    absent('Day-count presence', 'not measured — needs a tracking window'),
    absent('Open risks', 'not measured — Risk plane unbuilt'),
  ];
  return section('glance', 'At a glance · customizable', 'curated defaults — no metric it cannot source',
    html`<div class="glance-grid">${cards}</div>
      <p class="glance-scope hub-muted">Full customization (grid sizing, chart types, expand-to-detail, snip-to-client
        export) is a separate workstream — the zone and these curated defaults are what's shown today.</p>`,
    'signals + contribution');
}

/* ── 4 · Entity Core — identity facts only ─────────────────────────────────── */
const FACT_ORDER: Array<{ label: string; re: RegExp }> = [
  { label: 'HQ', re: /(hq|head\s*quarter|location|based)/i },
  { label: 'Founded', re: /(found|inception|established|since)/i },
  { label: 'CEO', re: /(ceo|chief\s*exec|leader|president)/i },
  { label: 'Employees', re: /(employee|headcount|staff|workforce)/i },
];
function entityCore(state: AppPayloadState): TemplateResult {
  const ent = state.entity;
  const facts = ent?.facts || {};
  const factRows: Array<{ label: string; value: string; source: string | null }> = [];
  const used = new Set<string>();
  // known identity facts first (labelled), then any remaining facts verbatim
  for (const spec of FACT_ORDER) {
    const key = Object.keys(facts).find((k) => !used.has(k) && spec.re.test(k));
    if (!key) continue;
    const v = factVal(facts[key] as FactEntry);
    if (!v) continue;
    used.add(key);
    factRows.push({ label: spec.label, value: v, source: factSrc(facts[key] as FactEntry) });
  }
  for (const [k, fv] of Object.entries(facts)) {
    if (used.has(k) || factRows.length >= 6) continue;
    const v = factVal(fv as FactEntry);
    if (!v) continue;
    factRows.push({ label: k, value: v, source: factSrc(fv as FactEntry) });
  }
  const hasCore = !!ent?.descriptor || factRows.length > 0;
  // Corporate map strip (mockup): the researcher-declared entities[] map, or the
  // honest absent state when it wasn't researched (both fixtures lack it).
  const em = state.entityMap || [];
  const subs = em.filter((e) => e.kind !== 'parent');
  // C3 (2026-07-17 steer): when the researcher-declared map is absent, the
  // presence tree still derives sub-entities from asset scopes — so this card
  // must NOT claim "nothing to show". Point at the tree and mark it derived.
  const corpMap = subs.length
    ? html`<div class="ec-corpmap"><span class="ec-corpmap-k">Corporate map:</span>
        ${subs.slice(0, 8).map((e) => html`<span class="ec-corpmap-ent">${e.name}<i>${e.kind}</i></span>`)}</div>`
    : html`<div class="ec-corpmap"><span class="ec-corpmap-k">Corporate map:</span>
        <span class="hub-muted">Researcher-declared entity map absent this run — the presence map below shows sub-entities derived from asset scopes (not authoritative).</span></div>`;
  if (!hasCore) {
    return section('entity', 'Entity core', 'the client’s identity facts',
      html`<p class="hub-muted hub-empty">Entity map not researched this run — identity facts appear once the
        Knowledge-Graph / Wikidata seed and corporate registries are gathered.</p>${corpMap}`,
      'Knowledge Graph seed · Wikidata');
  }
  return section('entity', 'Entity core', 'identity facts only — each with its source',
    html`<div class="ec-body">
      ${ent?.descriptor ? html`<div class="ec-descriptor">${ent.descriptor}</div>` : nothing}
      ${factRows.length ? html`<dl class="ec-facts">${factRows.map((f) => html`<div class="ec-fact">
        <dt>${f.label}</dt>
        <dd>${f.value}${f.source ? html`<span class="ec-dot" title=${`source: ${f.source}`} aria-label="sourced"></span>` : nothing}</dd>
      </div>`)}</dl>` : html`<p class="hub-muted">No structured identity facts on this run.</p>`}
      ${corpMap}
    </div>`,
    'Knowledge Graph seed · Wikidata');
}

/* ── 5 · Presence Map (the heart) — the mockup's fb-node card grammar ───────── */
type ChannelKey = 'websites' | 'social' | 'reviews' | 'community' | 'media';
const CHANNEL_LABEL: Record<ChannelKey, string> = {
  websites: 'Websites', social: 'Social', reviews: 'Reviews & Directories',
  community: 'Community & Reference', media: 'Media',
};
const CHANNEL_NOTE: Record<ChannelKey, string> = {
  websites: 'domains, app, IR, careers — fully controlled',
  social: 'brand-run profiles — its own segment',
  reviews: 'third-party listings, mostly claimable',
  community: 'collaboratively edited, high AI-citation weight',
  media: 'press, no editorial control',
};
const CHANNEL_ORDER: ChannelKey[] = ['websites', 'social', 'reviews', 'community', 'media'];
const TYPE_CHANNEL: Record<AssetType, ChannelKey | 'wiki'> = {
  website: 'websites', microsite: 'websites', app: 'websites', 'careers-profile': 'websites',
  'social-profile': 'social', 'video-channel': 'social', podcast: 'social',
  'review-site': 'reviews', 'directory-listing': 'reviews', 'financial-profile': 'reviews', 'regulatory-listing': 'reviews',
  forum: 'community', other: 'community',
  'news-coverage': 'media',
  wiki: 'wiki',
};
/** max nodes shown per channel before the "+N more" expander (keeps the section
 * scannable — the mockup collapses long groups). */
const CHANNEL_CAP = 6;

function channelOf(a: NormalizedAsset): ChannelKey | 'wiki' {
  return TYPE_CHANNEL[a.assetType] ?? 'community';
}

/** control tier chip (owned/editable/claimable/community/media), from the
 * asset's category — the presence map's per-property control colour. */
function ctrlTier(a: NormalizedAsset, channel: ChannelKey): string {
  if (channel === 'community') return 'community';
  if (channel === 'media') return 'media';
  switch (a.category) {
    case 'owned': return 'owned';
    case 'editable': return 'editable';
    case 'claimable': return 'claimable';
    case 'media': return 'media';
    default: return 'community';
  }
}

/** FIX-audit F10: which reputation PLANES this run actually carries. A plane
 * that never ran has NO denominator — its "0" is unknown, not a measured zero,
 * so contribution badges/rollups must render "not measured", never "S0·AI0" or
 * a red "not surfacing ⚠" (which asserts a measurement that never happened). */
function planesRan(state: AppPayloadState): { search: boolean; ai: boolean } {
  return {
    search: !!(state.serp || (state.serps && state.serps.length)),
    ai: !!state.aiNarrative,
  };
}

/** node state chip (active/stale/unclaimed) — only where derivable from the
 * real asset status; unknown status renders NO chip (never fabricated). */
function nodeState(a: NormalizedAsset): { label: string; cls: string } | null {
  switch (a.status) {
    case 'active': case 'claimed': return { label: 'active', cls: 'active' };
    case 'stale': case 'inactive': case 'abandoned': return { label: 'stale', cls: 'stale' };
    case 'unclaimed': return { label: 'unclaimed', cls: 'unclaimed' };
    default: return null;
  }
}

/** One property node card — the mockup's fb-node grammar: name + connectivity
 * dot (orphan = hollow), control-tier chip, MEASURED contribution badge
 * (`S6·AI4` — real numbers, NEVER an N: News is not wired), rank chip when a
 * SERP position exists, and a state chip where derivable. Dark assets get the
 * red "not surfacing ⚠" badge. Click → the universal asset drawer. */
function nodeCard(a: NormalizedAsset, channel: ChannelKey, contrib: ContributionMap, orphanIds: Set<string>): TemplateResult {
  const c = contributionFor(contrib, a.id);
  const tier = ctrlTier(a, channel);
  const rc = c ? rankChip(c) : null;
  const st = nodeState(a);
  // FIX-audit F10: only count planes that actually ran. With NEITHER search nor
  // AI in this run there is no denominator → "not measured", never a red "not
  // surfacing" (an assertion). The badge text shows only the ran planes' figures
  // (no fabricated S0/AI0 for a plane that never ran).
  const pl = planesRan(payloadStore.get());
  const surfParts: string[] = [];
  if (c && pl.search) surfParts.push(`S${c.searchAppearances}`);
  if (c && pl.ai) surfParts.push(`AI${c.aiCitations}`);
  const badge = (!c || (!pl.search && !pl.ai))
    ? html`<span class="fb-node-badge unknown">not measured</span>`
    : !c.surfacing
      ? html`<span class="fb-node-badge dark">not surfacing ⚠</span>`
      : html`<span class="fb-node-badge">${surfParts.join('·')}</span>`;
  return html`<div class="fb-node" @click=${() => openPropertyDrawer(payloadStore.get(), WS, a.id)} title=${a.url || a.title}>
    <div class="fb-node-top">
      <span class="fb-node-name">${a.title || a.url || a.id}</span>
      <span class="fb-conn-dot ${orphanIds.has(a.id) ? 'orphan' : ''}" aria-hidden="true"></span>
    </div>
    <span class="fb-ctrl-chip ${tier}">${tier}</span>
    ${badge}
    ${rc ? html`<span class="fb-rank-chip">${rc}</span>` : nothing}
    ${st ? html`<span class="fb-node-state ${st.cls}">${st.label}</span>` : nothing}
  </div>`;
}

/** A channel group: label + note + a responsive grid of node cards, collapsed
 * to the top CHANNEL_CAP with a "+N more" expander. */
function channelGroup(entityKey: string, channel: ChannelKey, assets: NormalizedAsset[], contrib: ContributionMap, orphanIds: Set<string>, rerender: () => void): TemplateResult {
  const key = `${entityKey}:${channel}`;
  const open = expandedChannels.has(key);
  const shown = open ? assets : assets.slice(0, CHANNEL_CAP);
  const more = assets.length - shown.length;
  return html`<div class="fb-channel-group">
    <div class="fb-channel-group-head">
      <span class="fb-channel-label ${channel === 'social' ? 'social' : ''}">${CHANNEL_LABEL[channel]}</span>
      <span class="fb-channel-note">${CHANNEL_NOTE[channel]}</span>
    </div>
    ${channel === 'community'
      ? html`<p class="fb-wiki-pointer">Wikipedia &amp; Wikidata are tracked as their own managed-entity card — <a href=${wikiHref()}>open the Wikipedia / Wikidata workroom →</a></p>`
      : nothing}
    <div class="fb-node-row">
      ${shown.map((a) => nodeCard(a, channel, contrib, orphanIds))}
      ${more > 0
        ? html`<div class="fb-node more" role="button" tabindex="0"
            @click=${() => { expandedChannels.add(key); rerender(); }}
            @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); expandedChannels.add(key); rerender(); } }}>+${more} more ${CHANNEL_LABEL[channel].toLowerCase()}</div>`
        : nothing}
    </div>
  </div>`;
}

/** The real entity rollup line (no fabricated numbers, News NEVER shown): total
 * properties · combined S/AI from contribution · owned/editable share of what
 * actually surfaces. Any non-derivable piece is omitted. */
function entityRollup(state: AppPayloadState, contrib: ContributionMap, row: MatrixEntityRow | null): string {
  let props = 0, s = 0, ai = 0, surf = 0, ownedSurf = 0;
  for (const a of state.assets) {
    if (row && !row.match(a)) continue;
    if (channelOf(a) === 'wiki') continue; // tree excludes wiki (card 6)
    props++;
    const c = contributionFor(contrib, a.id);
    if (!c) continue;
    s += c.searchAppearances;
    ai += c.aiCitations;
    if (c.surfacing) {
      surf++;
      if (a.category === 'owned' || a.category === 'editable') ownedSurf++;
    }
  }
  // FIX-audit F10: show a plane's combined figure only if that plane ran — a
  // search-only run shows `S{n}`, never a fabricated `AI0`, and vice-versa.
  const pl = planesRan(state);
  const parts = [`${props} ${props === 1 ? 'property' : 'properties'}`];
  const sai: string[] = [];
  if (pl.search) sai.push(`S${s}`);
  if (pl.ai) sai.push(`AI${ai}`);
  parts.push(sai.length ? `${sai.join('·')} combined` : 'search & AI not run this run');
  // C4b (2026-07-17 steer): this is the control-tier composition of THIS entity's
  // OWN mapped properties that surface (denominator = the entity's surfacing
  // assets) — NOT the page-1 SLOT-ownership share (that's Search's ownedPct, which
  // counts third-party results too). Label it for exactly what it measures so the
  // ~100% here never reads as a contradiction of the 40% page-1 figure.
  if ((pl.search || pl.ai) && surf > 0) parts.push(`~${Math.round((100 * ownedSurf) / surf)}% of its surfacing properties are owned/editable`);
  return parts.join(' · ');
}

/** channel groups for one entity's assets (wiki excluded), in canonical order. */
function entityChannels(entityKey: string, assets: NormalizedAsset[], contrib: ContributionMap, orphanIds: Set<string>, rerender: () => void): TemplateResult {
  const byChannel = new Map<ChannelKey, NormalizedAsset[]>();
  for (const a of assets) {
    const ch = channelOf(a);
    if (ch === 'wiki') continue;
    const bucket = byChannel.get(ch);
    if (bucket) bucket.push(a); else byChannel.set(ch, [a]);
  }
  const groups = CHANNEL_ORDER.filter((k) => byChannel.get(k)?.length);
  if (!groups.length) return html`<p class="hub-muted fb-emptygrp">No non-wiki properties mapped for this entity.</p>`;
  return html`${groups.map((k) => channelGroup(entityKey, k, byChannel.get(k)!, contrib, orphanIds, rerender))}`;
}

/** Short uppercase root-mark for the map-hub header (the tree's rooted-at glyph,
 * NOT a brand-logo fallback — this is a structural label, like the mockup's "EL"). */
function presenceMap(state: AppPayloadState, contrib: ContributionMap, _slug: string, rerender: () => void, row: CockpitClientRow | null): TemplateResult {
  const rows = matrixEntities(state);
  const primary = rows.find((r) => r.kind === 'parent') || rows[0] || null;
  const subs = rows.filter((r) => r !== primary);
  const match = (r: MatrixEntityRow | null): NormalizedAsset[] =>
    r ? state.assets.filter((a) => r.match(a)) : state.assets;
  const orphanIds = new Set((state.linkGraph?.orphans || []).map((o) => o.assetId));
  const total = state.assets.length;
  const primaryName = primary?.name || state.brand || 'Primary entity';

  // FIX-audit F11: a "measured link map" is only honest when the run actually
  // measured links — a link-graph pass (state.linkGraph) or edges flagged
  // `observed`. With neither, the graph is the structural fallback (synthetic
  // `associated-with` edges) and must be labelled "link graph not measured",
  // never "observed links".
  const linkMeasured = !!state.linkGraph || state.edges.some((e) => e.observed === true);
  const toggle = (mode: PMView): void => { pmView = mode; rerender(); };
  const controls = html`<div class="fb-map-toggle-row">
    <div class="pm-toggle" role="group" aria-label="presence view">
      <button class=${pmView === 'tree' ? 'on' : ''} @click=${() => toggle('tree')}>Tree</button>
      <button class=${pmView === 'graph' ? 'on' : ''} @click=${() => toggle('graph')}>Graph</button>
    </div>
    <span class="fb-source-note hub-muted">${linkMeasured
      ? 'Graph = the measured link map (crawl + Wikidata + page-facts)'
      : 'Graph = structural layout — link graph not measured this run'}</span>
  </div>`;

  let body: TemplateResult;
  if (pmView === 'graph') {
    body = html`<div class="pm-graphwrap">
      <svg id="map" class="pm-graph" role="img" aria-label=${linkMeasured ? 'measured link map' : 'structural layout — link graph not measured'}></svg>
      <div class="pm-graphnote hub-muted">${linkMeasured
        ? html`Measured link map — nodes are properties, edges are observed links; dashed
          edges are missing bridges and flagged orphans feed What Needs Improving.`
        : html`<b>Link graph not measured this run.</b> Nodes are properties; edges are the structural
          layout only (rooted at the primary entity), not observed links — no crawl / Wikidata / page-facts
          link measurement is in this run.`}</div>
    </div>`;
  } else {
    // DESIGN-FIDELITY P0-3: the hub mark obeys the locked brand-visual contract
    // (real logo → domain favicon → neutral NON-LETTER mark, never initials) via
    // the shared brandVisual()/brandLogo() the register already uses — no local
    // monogram path. The white .fb-hub-mark chip gives logo-on-dark contrast.
    const hubVisual = brandVisual({
      domain: row?.domain || primary?.domain,
      logoUrl: row?.logoUrl || state.brandLogo,
    });
    // C3 (2026-07-17 steer): when the run didn't declare payload.entities, the
    // sub-entities below are DERIVED from asset scopes — label it so the tree
    // never silently contradicts Entity Core's "map absent" state.
    const derived = !entityMapDeclared(state) && subs.length > 0;
    const mapHub = html`<div class="fb-map-hub">
      <span class="fb-hub-mark">${brandLogo(hubVisual)}</span>
      <div>
        <strong>Entity-first tree · rooted at ${primaryName}</strong>
        <span>${total} assets tracked across 1 primary entity${subs.length ? ` + ${subs.length} sub-${subs.length === 1 ? 'entity' : 'entities'}` : ''} · grouped by channel, not control lane</span>
        ${derived ? html`<span class="fb-map-derived">Sub-entities derived from asset scopes — not researcher-declared</span>` : nothing}
      </div>
    </div>`;
    const primaryBlock = html`<details class="fb-entity-block primary" open>
      <summary>
        <span class="fb-entity-toggle-icon" aria-hidden="true"></span>
        <span class="fb-entity-name">${primaryName} <span class="fb-chip azure on-dark">primary entity</span></span>
        <span class="fb-entity-rollup">${entityRollup(state, contrib, primary)}</span>
      </summary>
      <div class="fb-entity-body">${entityChannels('primary', match(primary), contrib, orphanIds, rerender)}</div>
    </details>`;
    const subBlocks = subs.map((s) => {
      const key = s.key;
      const isOpen = expandedEntities.has(key);
      const sAssets = match(s);
      return html`<details class="fb-entity-block sub" ?open=${isOpen}>
        <summary @click=${(e: Event) => { e.preventDefault(); if (isOpen) expandedEntities.delete(key); else expandedEntities.add(key); rerender(); }}>
          <span class="fb-entity-toggle-icon" aria-hidden="true"></span>
          <span class="fb-entity-name">${s.name || 'Unnamed entity'} <span class="fb-chip on-dark">${s.role}</span></span>
          <span class="fb-entity-rollup">${entityRollup(state, contrib, s)}</span>
        </summary>
        ${isOpen ? html`<div class="fb-entity-body">${entityChannels(key, sAssets, contrib, orphanIds, rerender)}</div>` : nothing}
      </details>`;
    });
    body = html`<div class="pm-tree">${mapHub}${primaryBlock}${subBlocks}</div>`;
  }

  // Always-visible graph teaser (mockup): the measured bridge/orphan counts, even
  // in Tree view — they feed What Needs Improving. Real counts from the link graph.
  const bridges = (state.linkGraph?.bridges || []).length;
  const orphans = (state.linkGraph?.orphans || []).length;
  const teaser = (bridges || orphans)
    ? html`<div class="pm-teaser hub-muted">Measured link map — <b>${bridges} bridge${bridges === 1 ? '' : 's'}</b>
        (missing links between owned/editable assets) and <b>${orphans} orphan${orphans === 1 ? '' : 's'}</b>
        flagged; they feed What Needs Improving below.</div>`
    : html`<div class="pm-teaser hub-muted">Link graph measured — no missing bridges or orphans flagged this run.</div>`;
  const hint = html`<div class="pm-hint hub-muted">Click a property → its detail drawer (cross-surface contribution, connectivity, attached work).</div>`;

  return section('presence', 'Presence map', 'the entity-first map of every property — Wikipedia lives in its own card below',
    html`<div class="pm-root">${controls}${teaser}${body}${hint}</div>`,
    'entities[] + assets[] + link graph');
}

/* ── 6 · Wikipedia & Wikidata card ─────────────────────────────────────────── */
function wikiCard(state: AppPayloadState): TemplateResult {
  const wd = state.wikidata;
  const kp = state.knowledgePanel;
  const wikiAssets = state.assets.filter((a) => a.assetType === 'wiki');
  const worksheet = wd?.worksheet?.length ?? 0;
  const known = !!(wd?.present || kp?.present || wikiAssets.length);
  // Top-driver banner only when a wiki domain is genuinely the #1 AI cited source.
  const topDom = [...(state.aiNarrative?.citedDomains || [])].sort((a, b) => b.count - a.count)[0];
  const wikiIsTopAi = !!topDom && /wikipedia|wikidata|wikimedia/i.test(topDom.domain);
  const body = known
    ? html`<div class="wk-body">
        <div class="wk-facts">
          <div class="wk-fact"><span>Wikipedia article</span><b>${wikiAssets.length ? 'present' : 'not tracked'}</b><small>${wikiAssets.length ? `${wikiAssets.length} article / language edition${wikiAssets.length === 1 ? '' : 's'} · protection & talk not measured this run` : 'no article asset on this run'}</small></div>
          <div class="wk-fact"><span>Wikidata worksheet</span><b>${wd?.qid || (wd?.present ? 'item present' : 'no item')}</b><small>${worksheet ? `${worksheet} claim${worksheet === 1 ? '' : 's'} reviewed` : (wd?.label || 'no worksheet')}</small></div>
          <div class="wk-fact"><span>Knowledge Panel</span><b>${kp ? (kp.present ? 'present' : 'absent') : 'not measured'}</b><small>${kp?.present ? `claim: ${kp.claimed}` : ''}</small></div>
        </div>
        ${wikiIsTopAi ? html`<div class="wk-contrib">Top driver — Wikipedia is the client’s #1 cited source in AI answers (${topDom!.count} citations).</div>` : nothing}
        <a class="hub-open" href=${wikiHref()}>Open Wikipedia / Wikidata workroom →</a>
      </div>`
    : html`<p class="hub-muted hub-empty">Wikipedia / Wikidata not researched on this run. The managed-entity
        service line — article, editions, Knowledge Panel and structured claims — appears once the entity plane runs.</p>`;
  return section('wiki', 'Wikipedia & Wikidata · managed entity', 'Five Blocks service line', body, 'Wikimedia + Wikidata');
}

/* ── 7 · How the Reputation Surfaces ───────────────────────────────────────── */
/** by-entity lens (the ONLY per-metric entity lens — ratified). Per-plane muted
 * copy names the plane ("not tracked in {plane} scope"); Search opens by default. */
function byEntityDisclosure(state: AppPayloadState, contrib: ContributionMap, kind: 'search' | 'ai', planeLabel: string, open = false): TemplateResult {
  const rows = matrixEntities(state).slice(0, 8);
  if (rows.length <= 1) return html``;
  return html`<details class="rep-byent" ?open=${open}><summary>by entity</summary>
    <ul class="rep-entlist">${rows.map((r) => {
      const ec = entityContribution(state, contrib, r);
      const n = kind === 'search' ? ec.searchAppearances : ec.aiCitations;
      return html`<li><span class="rep-entname">${ec.name || 'Unnamed'}</span>${n > 0
        ? html`<span class="rep-entval">${kind === 'search' ? `${n} appearance${n === 1 ? '' : 's'}` : `${n} citation${n === 1 ? '' : 's'}`}</span>`
        : html`<span class="rep-entval hub-muted">not tracked in ${planeLabel} scope</span>`}</li>`;
    })}</ul></details>`;
}

/** measured owned/editable/media/adversarial page-1 stack bar (searchAttribution). */
function stackBar(stack: { owned: number; editable: number; media: number; adversarial: number }): TemplateResult {
  const seg = (cls: string, w: number, label: string): TemplateResult | typeof nothing =>
    w > 0 ? html`<i class="rep-seg rep-seg-${cls}" style=${`width:${w}%`} title=${`${label} ${w}%`}></i>` : nothing;
  return html`<div class="rep-stack" role="img" aria-label="page-one ownership stack">
    ${seg('owned', stack.owned, 'owned')}${seg('editable', stack.editable, 'editable')}
    ${seg('media', stack.media, 'media')}${seg('adversarial', stack.adversarial, 'adversarial')}
  </div>`;
}

function reputationSurfaces(state: AppPayloadState, contrib: ContributionMap): TemplateResult {
  const sa = searchAttribution(state);
  const ai = aiHeadline(state);
  const searchCard = html`<article class="rep-card">
    <div class="rep-h"><h3>Search</h3><a class="hub-open" href=${surfaceHref('search')}>Open full Search →</a></div>
    ${sa
      ? html`<div class="rep-metric">${sa.ownedPct}%<small>page-1 owned · ${sa.page1Owned} of ${sa.page1Slots} slots</small></div>
        ${stackBar(sa.stackPct)}
        <p class="rep-attr">Of what surfaces here, ${sa.ownedPct}% maps to properties you control.</p>
        ${byEntityDisclosure(state, contrib, 'search', 'Search', true)}`
      : html`<p class="hub-muted">No page-one SERP capture on this run — search contribution not measured.</p>`}
  </article>`;
  const newsCard = html`<article class="rep-card rep-news">
    <div class="rep-h"><h3>News</h3><span class="fresh-chip none">not wired</span></div>
    <p class="hub-muted">IMPACT News has no data plane wired into the run payload — nothing is fabricated here.
      When wired, publisher identity and appearance persistence appear on the same grammar as Search.</p>
  </article>`;
  const aiCard = html`<article class="rep-card">
    <div class="rep-h"><h3>AI narrative</h3><a class="hub-open" href=${surfaceHref('ai')}>Open full AI →</a></div>
    ${ai && ai.ownership != null
      ? html`<div class="rep-metric">${pct(ai.ownership)}%<small>source-ownership · ${ai.engines} engines</small></div>
        <p class="rep-attr">Of ${ai.total.toLocaleString()} citations here, ${pct(ai.ownership)}% map to properties you control.</p>
        ${byEntityDisclosure(state, contrib, 'ai', 'AI')}`
      : html`<p class="hub-muted">AI citation ownership not measured on this run${ai ? ' (no owned-vs-earned split in the AIQ poll)' : ''}.</p>`}
  </article>`;
  return section('reputation', 'How the reputation surfaces', 'where the client shows up, and how much of it they control',
    html`<div class="rep-grid">${searchCard}${newsCard}${aiCard}</div>`,
    'IMPACT · IMPACT News · AIQ + contribution');
}

/* ── 8 · What Needs Improving — grouped, with an entity filter (mockup) ─────── */
function needsImproving(state: AppPayloadState, contrib: ContributionMap, slug: string, rerender: () => void): TemplateResult {
  const orphanIds = new Set((state.linkGraph?.orphans || []).map((o) => o.assetId));
  const ents = matrixEntities(state);
  const entRow = niEntityFilter ? ents.find((e) => e.key === niEntityFilter) || null : null;
  const inFilter = (a: NormalizedAsset): boolean => !entRow || entRow.match(a);

  const verb = (label: string, cls: string): TemplateResult => html`<span class="ni-verb ni-verb-${cls}">${label}</span>`;
  const group = (label: string, items: TemplateResult[]): TemplateResult => items.length
    ? html`<div class="ni-group"><div class="ni-group-label">${label}</div><div class="ni-rows">${items}</div></div>`
    : html``;

  // Dark assets — measured-not-surfacing OR orphaned; typed solution as a verb.
  const dark: TemplateResult[] = [];
  for (const a of state.assets) {
    if (!inFilter(a)) continue;
    const c = contributionFor(contrib, a.id);
    if (!(orphanIds.has(a.id) || (c ? !c.surfacing : false))) continue;
    let reason = 'not surfacing'; let v = 'Boost'; let vc = 'boost';
    if (orphanIds.has(a.id)) { reason = 'orphan — no inbound links'; v = 'Link'; vc = 'link'; }
    else if (a.category === 'claimable' || a.status === 'unclaimed') { reason = 'unclaimed'; v = 'Claim'; vc = 'claim'; }
    else if (a.durability === 'volatile') { reason = 'stale / volatile'; v = 'Boost'; vc = 'boost'; }
    if (dark.length < 8) dark.push(html`<button class="ni-row" @click=${() => openPropertyDrawer(payloadStore.get(), WS, a.id)}>
      <span class="ni-name"><strong>${a.title || a.url || a.id}</strong><small>${reason}</small></span>${verb(v, vc)}</button>`);
  }
  // Opportunities (payload)
  const opps = (state.opportunities || []).slice(0, 6).map((o) => {
    // DESIGN-FIDELITY P0-1: the Opportunity contract has no `title`; bind the
    // real action+platform title + rationale (like the Conflicts rows beside
    // it), not the bare category word "Opportunity".
    return html`<div class="ni-row"><span class="ni-name"><strong>${opportunityTitle(o)}</strong>${o.rationale ? html`<small>${o.rationale}</small>` : nothing}</span>${verb('Review', 'review')}</div>`;
  });
  // Conflicts (payload) — honestly run-asserted (LLM), not measured extraction.
  const conflicts = (state.conflicts || []).slice(0, 6).map((cf) => {
    const title = (cf as { title?: string; description?: string; field?: string }).title
      || (cf as { field?: string }).field || (cf as { description?: string }).description || 'Conflict';
    return html`<div class="ni-row"><span class="ni-name"><strong>${title}</strong><small>run-asserted (LLM) — not measured extraction</small></span>${verb('Resolve', 'resolve')}</div>`;
  });
  // Measured conflicts (W2-E) — deterministic disagreements between OBSERVED
  // extracted values, distinct from the LLM-asserted rows above. Only present
  // when the field-extraction pass actually ran for THIS client.
  ensureExtractionSub(rerender);
  const ex = extractionState();
  const measuredConflicts: FieldConflict[] = ex.slug === slug && ex.status === 'ok' ? ex.conflicts : [];
  const measured = measuredConflicts.slice(0, 6).map((cf) => html`<div class="ni-row">
    <span class="ni-name"><strong>${measuredConflictTitle(cf)}</strong>
      <small class="ni-measured">measured — ${measuredConflictEvidence(cf)}</small></span>${verb('Resolve', 'resolve')}</div>`);
  const extractable = extractableUrls(state);
  const extractionCta = ex.status === 'loading'
    ? html`<p class="ni-measured-note">Measuring displayed fields across properties…</p>`
    : ex.slug === slug && ex.status === 'ok'
      ? (measured.length ? nothing : html`<p class="ni-measured-note">Field extraction ran — no measured conflicts across ${ex.coverage?.extracted ?? 0} extracted propert${(ex.coverage?.extracted ?? 0) === 1 ? 'y' : 'ies'}.</p>`)
      : ex.status === 'error'
        ? html`<p class="ni-measured-note">Field extraction unavailable — ${ex.reason}. Nothing is fabricated.</p>`
        : extractable.length >= 2
          ? html`<button class="ni-extract-btn" @click=${() => runExtraction(slug, extractable)}>Run field extraction to measure conflicts →</button>`
          : nothing;

  const filterRow = ents.length > 1
    ? html`<div class="ni-filter"><span class="ni-filter-label">Filter:</span>
        <select class="ni-filter-select" @change=${(e: Event) => { niEntityFilter = (e.target as HTMLSelectElement).value; rerender(); }}>
          <option value="" ?selected=${!niEntityFilter}>All entities</option>
          ${ents.map((en) => html`<option value=${en.key} ?selected=${niEntityFilter === en.key}>${en.name || en.role}</option>`)}
        </select></div>`
    : nothing;

  const totalOpps = (state.opportunities || []).length;
  ensureOvDecisions(slug, rerender);
  const decided = ovDecSlug === slug ? ovDecCount : null;
  const planLine = totalOpps
    ? decided != null
      ? html`<span class="ni-plan-count hub-muted">${decided} of ${totalOpps} opportunit${totalOpps === 1 ? 'y' : 'ies'} decided${decided ? '' : ' — none yet'}</span>`
      : html`<span class="ni-plan-count hub-muted">${totalOpps} opportunit${totalOpps === 1 ? 'y' : 'ies'} to decide</span>`
    : nothing;

  const any = dark.length || opps.length || conflicts.length || measured.length;
  const body = html`<div class="ni-body">
    ${filterRow}
    ${any ? html`${group('Dark assets', dark)}${group('Opportunities', opps)}${group('Conflicts', conflicts)}${measured.length ? group('Measured conflicts', measured) : nothing}`
      : html`<p class="hub-muted">Nothing flagged for improvement${entRow ? ' for this entity' : ''} on this run.</p>`}
    ${extractionCta}
    <div class="ni-group"><div class="ni-group-label">Risks</div>
      <p class="ni-risk hub-muted">No structured risk records this run — the Risk plane is unbuilt. The Conflicts group is
        run-asserted (LLM); Measured conflicts, when present, come from the deterministic field-extraction pass.</p></div>
    <div class="ni-links">
      <a class="hub-open" href="#/client/${slug}/plan">Open the Intelligence Plan →</a>${planLine}
      <a class="hub-open" href="#/client/${slug}/board">Open this client’s board →</a>
    </div>
  </div>`;
  return section('improve', 'What needs improving', 'dark assets, opportunities and conflicts from this run', body,
    'assets[] + conflicts[] + risks[]');
}

/* ── 9 · Coverage footer — per-plane enumeration (mockup) ──────────────────── */
function coverageFooter(state: AppPayloadState): TemplateResult {
  const cov = coveragePanel(state);
  // Per-plane ✓/status tokens from the coverage layers, then the standing receipt.
  const planes = (cov?.layers || []).map((l) => {
    const status = String((l as { status?: string; v?: string }).status ?? (l as { v?: string }).v ?? '').toLowerCase();
    const name = (l as { label?: string; key?: string }).label || (l as { key?: string }).key || 'layer';
    const token = /thin/.test(status) ? 'thin' : /skip|scoped|not\s*run|absent/.test(status) ? 'not run' : /checked|done|ok|cover/.test(status) ? '✓' : status || '—';
    return `${name} ${token}`;
  });
  const line = planes.length
    ? `Coverage — ${planes.join(' · ')}`
    : cov
      ? `Coverage — ${cov.checkedCount}/${cov.inScopeTotal} in-scope research layers covered${cov.thinCount ? ` · ${cov.thinCount} thin` : ''}${cov.scopedCount ? ` · ${cov.scopedCount} scoped out` : ''}`
      : 'Coverage accounting not available on this run';
  return html`<footer class="hub-cov">
    <span class="hub-cov-line">${line}</span>
    <span class="hub-cov-receipt">Every finding on this page carries its source + receipt — a plane not measured reads
      "not measured", never a fabricated zero.</span>
  </footer>`;
}

/* ── right CRM sidecard rail — honest not-connected (spec §3 / mockup: 6 cards) ── */
export interface ProgramMemoryCardView {
  memory: ProgramMemoryResponse;
  diff: ProgramMemoryDiff | null;
  pendingSection: string | null;
  lockError?: string | null;
}

type ProgramMemoryLoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; view: ProgramMemoryCardView };

interface ProgramMemoryCacheEntry {
  state: ProgramMemoryLoadState;
  fetchedAt: number;
  inFlight: boolean;
}

export interface ProgramMemoryLinkContext {
  clientSlug: string;
  runFile: string | null;
}

const PROGRAM_MEMORY_READY_TTL_MS = 60_000;
const programMemoryCache = new Map<string, ProgramMemoryCacheEntry>();

function isProgramMemoryStale(entry: ProgramMemoryCacheEntry, now: number): boolean {
  if (entry.state.phase === 'ready') {
    return now - entry.fetchedAt > PROGRAM_MEMORY_READY_TTL_MS;
  }
  if (entry.state.phase === 'error') return true;
  return false;
}

interface SummaryBlock {
  kind: 'heading' | 'paragraph';
  text: string;
}

function currentProgramSummary(bodyMd: string): SummaryBlock[] {
  const lines = bodyMd.replace(/\r\n/g, '\n').split('\n');
  const start = lines.findIndex((line) => line.trim() === '## Current program summary');
  if (start < 0) return [];
  const section: string[] = [];
  for (let index = start + 1; index < lines.length; index += 1) {
    if (/^##\s+/.test(lines[index]!.trim())) break;
    section.push(lines[index]!);
  }
  const blocks: SummaryBlock[] = [];
  let paragraph: string[] = [];
  const flushParagraph = (): void => {
    const text = paragraph.join(' ').trim();
    if (text) blocks.push({ kind: 'paragraph', text });
    paragraph = [];
  };
  for (const line of section) {
    const trimmed = line.trim();
    const heading = /^#{3,6}\s+(.+)$/.exec(trimmed);
    if (heading) {
      flushParagraph();
      blocks.push({ kind: 'heading', text: heading[1]! });
    } else if (!trimmed) {
      flushParagraph();
    } else {
      paragraph.push(trimmed);
    }
  }
  flushParagraph();
  return blocks;
}

function memoryCitation(
  label: string,
  ref: string,
  linkContext: ProgramMemoryLinkContext,
): TemplateResult {
  const taskId = ref.startsWith('task:') ? ref.slice('task:'.length) : '';
  if (taskId && linkContext.runFile) {
    const href = buildTaskHref({
      clientSlug: linkContext.clientSlug,
      taskId,
      runFile: linkContext.runFile,
    });
    return html`<a class="fb-chip pmem-citation" href=${href}>${label}</a>`;
  }
  // run:/decision:/output:/signal:/note: do not have one uniform deep-link
  // contract on this card yet. Honest labeled chips beat links to the wrong surface.
  return html`<span class="fb-chip pmem-citation">${label}</span>`;
}

function noProgramMemory(): TemplateResult {
  return html`<section class="hrail-card">
    <span class="hrail-eyebrow">Program Memory</span>
    <h3 class="hrail-status">not generated</h3>
    <p class="hub-muted">Cross-run memory (what changed, what was decided) builds after a second run.</p>
  </section>`;
}

/** Pure Program Memory card renderer, exported for the overview unit specs. */
export function renderProgramMemoryBody(
  view: ProgramMemoryCardView,
  onToggleLock: (section: string, locked: boolean) => void,
  linkContext: ProgramMemoryLinkContext,
): TemplateResult {
  const { memory, diff, pendingSection, lockError } = view;
  const latest = memory.latest;
  if (!latest) return noProgramMemory();

  const claims = claimItems(latest.claims);
  const summary = currentProgramSummary(latest.body_md);
  const sections = [...new Set([
    ...claims.map((claim) => claim.section),
    ...memory.locks.map((lock) => lock.section),
  ])];
  const locks = new Map(memory.locks.map((lock) => [lock.section, lock]));
  const citations = claims.flatMap((claim) => claim.citations);
  const sectionCount = diff?.sectionsChanged.length ?? 0;

  return html`<section class="hrail-card pmem-card">
    <span class="hrail-eyebrow">Program Memory</span>
    <div class="pmem-content">
      <span class="fb-chip azure">visibly AI-curated</span>
      <h3 class="hrail-status">v${latest.version} · as of ${latest.created_at.slice(0, 10)}</h3>
      <div class="pmem-summary">
        ${summary.length
          ? summary.map((block) => block.kind === 'heading'
            ? html`<strong>${block.text}</strong>`
            : html`<p>${block.text}</p>`)
          : html`<p class="hub-muted">Current program summary not included in this version.</p>`}
      </div>
      <p class="pmem-count">${claims.length} claim${claims.length === 1 ? '' : 's'}</p>
      ${citations.length
        ? html`<div class="pmem-citations" aria-label="claim citations">
            ${citations.map((citation) => memoryCitation(citation.label, citation.ref, linkContext))}
          </div>`
        : nothing}
      ${diff
        ? html`<p class="hub-muted">What changed: ${diff.claimsAdded.length} added · ${diff.claimsChanged.length} changed · ${diff.claimsRemoved.length} removed · ${sectionCount} section${sectionCount === 1 ? '' : 's'}</p>`
        : nothing}
      ${memory.heldUpdate
        ? html`<p class="pmem-held">an update was held for validation — ${memory.heldUpdate.heldReason}</p>`
        : nothing}
      ${sections.length
        ? html`<ul class="pmem-list" aria-label="Program Memory section locks">
            ${sections.map((section) => {
              const lock = locks.get(section);
              const pending = pendingSection === section;
              return html`<li class="pmem-row">
                <span class="pmem-kind">${section}</span>
                <span class="pmem-meta">${lock ? `locked by ${lock.lockedBy}` : 'unlocked'}</span>
                ${lock && memory.lockedStale.includes(section)
                  ? html`<span class="pmem-held">locked-and-stale — newer evidence exists</span>`
                  : nothing}
                <button
                  class="fb-chip azure pmem-lock-toggle"
                  type="button"
                  ?disabled=${pending}
                  @click=${() => onToggleLock(section, Boolean(lock))}
                >${pending ? 'Updating' : lock ? 'Unlock' : 'Lock'} ${section}</button>
              </li>`;
            })}
          </ul>`
        : nothing}
      ${lockError ? html`<p class="hub-muted">${lockError}</p>` : nothing}
    </div>
  </section>`;
}

function previousPublishedVersion(memory: ProgramMemoryResponse): number | null {
  const latestVersion = memory.latest?.version;
  if (latestVersion == null) return null;
  return memory.versions
    .filter((version) => version.status === 'published' && version.version < latestVersion)
    .sort((left, right) => right.version - left.version)[0]?.version ?? null;
}

async function fetchProgramMemoryView(slug: string): Promise<ProgramMemoryCardView> {
  const memory = await getProgramMemory(slug);
  const previous = previousPublishedVersion(memory);
  const diff = memory.latest && previous !== null
    ? await getProgramMemoryDiff(slug, previous, memory.latest.version)
    : null;
  return { memory, diff, pendingSection: null };
}

function loadProgramMemory(slug: string, rerender: () => void): void {
  const current = programMemoryCache.get(slug);
  if (current?.inFlight) return;
  programMemoryCache.set(slug, current
    ? { ...current, inFlight: true }
    : { state: { phase: 'loading' }, fetchedAt: Date.now(), inFlight: true });
  fetchProgramMemoryView(slug)
    .then((view) => {
      programMemoryCache.set(slug, {
        state: { phase: 'ready', view },
        fetchedAt: Date.now(),
        inFlight: false,
      });
      rerender();
    })
    .catch((error: unknown) => {
      const failedEntry: ProgramMemoryCacheEntry = {
        state: { phase: 'error', message: String(error) },
        fetchedAt: Date.now(),
        inFlight: true,
      };
      programMemoryCache.set(slug, failedEntry);
      rerender();
      if (programMemoryCache.get(slug) === failedEntry) {
        programMemoryCache.set(slug, { ...failedEntry, inFlight: false });
      }
    });
}

async function toggleProgramMemoryLock(
  slug: string,
  section: string,
  locked: boolean,
  rerender: () => void,
): Promise<void> {
  const current = programMemoryCache.get(slug);
  if (current?.state.phase !== 'ready') return;
  const priorView = current.state.view;
  programMemoryCache.set(slug, {
    ...current,
    state: {
      phase: 'ready',
      view: { ...priorView, pendingSection: section, lockError: null },
    },
    inFlight: true,
  });
  rerender();
  try {
    if (locked) await deleteLock(slug, section);
    else await postLock(slug, section);
    const view = await fetchProgramMemoryView(slug);
    programMemoryCache.set(slug, {
      state: { phase: 'ready', view },
      fetchedAt: Date.now(),
      inFlight: false,
    });
  } catch (error) {
    programMemoryCache.set(slug, {
      ...current,
      state: {
        phase: 'ready',
        view: {
          ...priorView,
          pendingSection: null,
          lockError: `Lock update unavailable — ${String(error)}`,
        },
      },
      inFlight: false,
    });
  }
  rerender();
}

export function renderProgramMemoryCard(
  slug: string,
  runFile: string | null,
  rerender: () => void,
): TemplateResult {
  const entry = programMemoryCache.get(slug);
  if (!entry) {
    loadProgramMemory(slug, rerender);
    return html`<section class="hrail-card">
      <span class="hrail-eyebrow">Program Memory</span>
      <h3 class="hrail-status">loading…</h3>
      <p class="hub-muted">Loading the latest published version.</p>
    </section>`;
  }
  if (isProgramMemoryStale(entry, Date.now()) && !entry.inFlight) {
    loadProgramMemory(slug, rerender);
  }
  const { state } = entry;
  if (state.phase === 'loading') {
    return html`<section class="hrail-card">
      <span class="hrail-eyebrow">Program Memory</span>
      <h3 class="hrail-status">loading…</h3>
      <p class="hub-muted">Loading the latest published version.</p>
    </section>`;
  }
  if (state.phase === 'error') {
    return html`<section class="hrail-card">
      <span class="hrail-eyebrow">Program Memory</span>
      <h3 class="hrail-status">unavailable</h3>
      <p class="hub-muted">Program Memory could not be loaded — ${state.message}</p>
    </section>`;
  }
  return renderProgramMemoryBody(
    state.view,
    (section, locked) => {
      void toggleProgramMemoryLock(slug, section, locked, rerender);
    },
    { clientSlug: slug, runFile },
  );
}

function crmSidecards(slug: string, runFile: string | null, rerender: () => void): TemplateResult {
  // DESIGN-FIDELITY P1-5: the mockup's rail-card grammar — a MONO eyebrow
  // category label, a SERIF status headline, sans body — not a sans title + chip.
  const card = (title: string, status: string, copy: string): TemplateResult => html`<section class="hrail-card">
    <span class="hrail-eyebrow">${title}</span>
    <h3 class="hrail-status">${status}</h3>
    <p class="hub-muted">${copy}</p>
  </section>`;
  // No dead Connect buttons — plain honest copy (the established register/hub
  // treatment); the mockup's Connect buttons are illustrative of the future.
  return html`
    ${card('Relationship / Stakeholders', 'not connected', 'No stakeholder map on file for this client yet — it assembles once the relationship layer is wired.')}
    ${card('Meetings & Transcripts', 'not connected', 'Link Fireflies to pull meeting notes and sourced action items into this hub.')}
    ${card('Jira', 'not connected', 'Read-only once connected — issues render here, with no writes from this hub.')}
    ${card('Documents', 'not connected', 'Google Drive — link a client folder to surface briefs and decks here.')}
    ${card('Deliverables', 'none yet', 'Exports and reports generated from this hub collect here.')}
    ${renderProgramMemoryCard(slug, runFile, rerender)}`;
}

/** Compose the whole Client Hub Overview (locked order). Called by index.ts's
 * paint() once the client's run payload is loaded. */
export function renderOverview(deps: OverviewDeps): TemplateResult {
  const { state, row, slug, rerender } = deps;
  const contrib = computeContribution(state);
  return html`<div class="hub-grid">
    <div class="hub-main">
      ${headerStrip(state, row)}
      ${programPosition(state)}
      ${glanceStrip(state, row, contrib)}
      ${entityCore(state)}
      ${presenceMap(state, contrib, slug, rerender, row)}
      ${wikiCard(state)}
      ${reputationSurfaces(state, contrib)}
      ${needsImproving(state, contrib, slug, rerender)}
      ${coverageFooter(state)}
    </div>
    <aside class="hub-rail" aria-label="client relationship context">${renderSignalsStrip(slug, rerender)}${crmSidecards(slug, row?.lastRunFile ?? null, rerender)}</aside>
  </div>`;
}
